import { scanCompleted, setCardId, setOcrData } from '../../../actions/addInsurance';
import { setRuntimeVariable } from '../../../actions/runtime';
import { IMAGE_TYPE_BACK, IMAGE_TYPE_FRONT } from '../../../constants';
import { getInsurerIdByInsurerCode } from '../../../core/util/insurers';
import { buildDateStringFromUserInput } from '../../../core/util/date';
import { getInsurerCodeFromName } from './insurers';
import { isString } from '../../../core/util/string';
import { doValuesMatch } from '../../../core/util/object';
import { deconstructDateString } from '../../../core/util/date';
import { isEmptyObject } from '../../../core/util/object';

const fields = {
  dobYear: { id: 'dobYear', label: 'Year' },
  dobMonth: { id: 'dobMonth', label: 'Birthdate: Month' },
  dobDay: { id: 'dobDay', label: 'Day' },
  insuranceCarrier: { id: 'insuranceCarrier', label: 'Insurance Carrier' },
  insuranceType: { id: 'insuranceType', label: 'Insurance Type' },
  memberId: { id: 'memberId', label: 'Member ID' },
  firstName: { id: 'firstName', label: 'First Name' },
  lastName: { id: 'lastName', label: 'Last Name' },
};

const isMatchingProfiles = (profile1, profile2) =>
  doValuesMatch(
    [profile1, profile2],
    ['firstName', 'lastName'],
    (val1, val2) =>
      isString(val1) && isString(val2) && val1.trim().toLowerCase() === val2.trim().toLowerCase()
  );

const getInitialValues = state => {
  /* If there is no data from addInsurance state, use the newBooking data
   * If there is addInsurance data, check if profiles match and return a combination
   * Otherwise, return the addInsurance data
   */
  const { day, month, year } = deconstructDateString(state.newBooking.profile.birthDate);
  if (isEmptyObject(state.addInsurance.insurance)) {
    return {
      dobDay: day,
      dobMonth: month,
      dobYear: year,
      ...state.newBooking.profile,
    };
  }

  const { dobDay, dobMonth, dobYear, ...insuranceProfile } = state.addInsurance.insurance;
  if (isMatchingProfiles(state.addInsurance.insurance, state.newBooking.profile)) {
    return {
      dobDay: dobDay || day,
      dobMonth: dobMonth || month,
      dobYear: dobYear || year,
      ...state.newBooking.profile,
      ...insuranceProfile,
    };
  }

  return state.addInsurance.insurance;
};

const buildInsuranceProfilePostData = (insuranceForm = {}, accountId, insurers = []) => {
  const insuranceCarrierName =
    isString(insuranceForm.insuranceCarrier) && insuranceForm.insuranceCarrier.trim();
  return {
    account_id: accountId,
    insurer_id: getInsurerIdByInsurerCode(
      getInsurerCodeFromName(insurers, insuranceCarrierName),
      insurers
    ),
    insurer_type: insuranceForm.insuranceType,
    insurer_name: insuranceCarrierName,
    member_code: insuranceForm.memberId,
    first_name: insuranceForm.firstName,
    last_name: insuranceForm.lastName,
    birth_date: buildDateStringFromUserInput(
      insuranceForm.dobYear,
      insuranceForm.dobMonth,
      insuranceForm.dobDay
    ),
    card_front_id: insuranceForm.cardFront,
    card_back_id: insuranceForm.cardBack,
  };
};

const buildInsuranceCoveragePostData = (
  insuranceForm = {},
  location = {},
  insurers = [],
  userProfileId = null,
  insuranceProfileId = null
) => {
  const insuranceCarrierName =
    isString(insuranceForm.insuranceCarrier) && insuranceForm.insuranceCarrier.trim();
  return {
    member_code: insuranceForm.memberId,
    insurer_name: insuranceCarrierName,
    insurer_code: getInsurerCodeFromName(insurers, insuranceCarrierName),
    plan_type: insuranceForm.insuranceType,
    first_name: insuranceForm.firstName,
    last_name: insuranceForm.lastName,
    birth_date: buildDateStringFromUserInput(
      insuranceForm.dobYear,
      insuranceForm.dobMonth,
      insuranceForm.dobDay
    ),
    npi_list_str: location.npi,
    location_id: location.id,
    user_profile_id: userProfileId,
    insurance_profile_id: insuranceProfileId,
  };
};

const buildNewBookingInsuranceData = (
  insuranceForm = {},
  insurers = [],
  userProfileId = null,
  accountId = null
) => {
  const insuranceCarrierName =
    isString(insuranceForm.insuranceCarrier) && insuranceForm.insuranceCarrier.trim();
  return {
    memberCode: insuranceForm.memberId,
    insurerName: insuranceCarrierName,
    insurerCode: getInsurerCodeFromName(insurers, insuranceCarrierName),
    insurerType: insuranceForm.insuranceType,
    firstName: insuranceForm.firstName,
    lastName: insuranceForm.lastName,
    cardFront: insuranceForm.cardFront,
    cardBack: insuranceForm.cardBack,
    birthDate: buildDateStringFromUserInput(
      insuranceForm.dobYear,
      insuranceForm.dobMonth,
      insuranceForm.dobDay
    ),
    userProfileId,
    accountId,
  };
};

const dispatchInsuranceForEdit = dispatch => insuranceProfile => {
  const { day, month, year } = deconstructDateString(insuranceProfile.birth_date);
  dispatch(
    setOcrData(
      {
        firstName: insuranceProfile.first_name,
        lastName: insuranceProfile.last_name,
        insuranceCarrier: insuranceProfile.insurer_name,
        insuranceType: insuranceProfile.insurer_type,
        memberId: insuranceProfile.member_code,
        dobDay: day,
        dobMonth: month,
        dobYear: year,
      },
      IMAGE_TYPE_FRONT
    )
  );
  dispatch(setCardId(insuranceProfile.card_front_id, IMAGE_TYPE_FRONT));
  dispatch(setCardId(insuranceProfile.card_back_id, IMAGE_TYPE_BACK));
  dispatch(scanCompleted(IMAGE_TYPE_FRONT));
  dispatch(scanCompleted(IMAGE_TYPE_BACK));
  dispatch(setRuntimeVariable({ name: 'isEditFlow', value: true }));
};

export {
  fields,
  buildInsuranceCoveragePostData,
  buildInsuranceProfilePostData,
  buildNewBookingInsuranceData,
  dispatchInsuranceForEdit,
  isMatchingProfiles,
  getInitialValues,
};
